import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { IcoSearch, IcoX } from '../../bz/icons';
import { VendorMark } from '../../bsa/projects/ProjectAtoms';
import { relativeTime } from '../../bsa/projects/projectListModel';
import '../../bsa/projects/projectsList.css';
import { facetCount, filterVendors, vendorFacets } from '../vendorFacets';
import { isGlobalVendor } from '../registry';
import { ItemToggle } from '../SuperAdminAtoms';

const EMPTY_SEL = { status: [], statusio: [], family: [] };

// Plan's projects-list layout (STRY0038867): facet rail, search, chips, table, empty
// state. The page keys this component by section (and preset) so the selection starts
// clean whenever the section changes.
const Vendors = ({ sectionId, items, focusUid, preset }) => {
  const vendors = items.filter(i => i.section === sectionId && i.source === 'vendor');
  const bypassById = Object.fromEntries(
    items.filter(i => i.source === 'bypass' && i.section === sectionId).map(b => [b.id, b])
  );
  const hasStatusIo = Object.keys(bypassById).length > 0;
  const active = Object.values(bypassById).filter(b => b.value);
  const facets = vendorFacets(vendors, bypassById);

  const [sel, setSel] = useState(preset === 'bypass' ? { ...EMPTY_SEL, statusio: ['bypass'] } : EMPTY_SEL);
  const [query, setQuery] = useState('');

  const shown = filterVendors(vendors, bypassById, facets, sel, query);
  const toggle = (key, id) =>
    setSel(s => ({ ...s, [key]: s[key].includes(id) ? s[key].filter(x => x !== id) : [...s[key], id] }));
  const chips = facets.flatMap(f =>
    sel[f.key].map(id => ({ key: f.key, id, label: f.options.find(o => o.id === id)?.label || id }))
  );
  const clearAll = () => {
    setSel(EMPTY_SEL);
    setQuery('');
  };

  return (
    <>
      {active.length > 0 && !sel.statusio.includes('bypass') && (
        <Alert color="warning" className="mb-0" fade={false}>
          <strong>
            {active.length} vendor{active.length > 1 ? 's are' : ' is'} skipping status.io checks:
          </strong>{' '}
          {active.map(a => a.vendor.name).join(', ')}. Runs go ahead even during a vendor incident.{' '}
          <button type="button" className="pl-linkbtn" onClick={() => setSel({ ...EMPTY_SEL, statusio: ['bypass'] })}>
            Show them
          </button>
        </Alert>
      )}
      <div className="pl-layout">
        <aside className="pl-rail" aria-label="Filters">
          <div className="pl-rail__head">
            <span className="bz-kicker">Filters</span>
            {chips.length > 0 && (
              <button type="button" className="pl-linkbtn" onClick={clearAll}>
                Clear all
              </button>
            )}
          </div>
          {facets.map(f => (
            <div key={f.key} className="pl-facet" role="group" aria-label={f.title}>
              <div className="pl-facet__title">{f.title}</div>
              {f.options.map(o => {
                const count = facetCount(vendors, bypassById, facets, sel, query, f.key, o.id);
                const checked = sel[f.key].includes(o.id);
                return (
                  <label key={o.id} className={`pl-facet__opt${count === 0 && !checked ? ' is-zero' : ''}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(f.key, o.id)} />
                    <span className="bz-truncate">{o.label}</span>
                    <span className="pl-facet__count">{count}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </aside>

        <div className="pl-main">
          <div className="pl-toolbar">
            <label className="bz-input pl-search">
              <IcoSearch size={14} />
              <input
                value={query}
                placeholder="Search vendors…"
                aria-label="Search vendors"
                onChange={e => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="pl-search__clear"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                >
                  <IcoX size={12} />
                </button>
              )}
            </label>
            <span className="bz-muted pl-count">
              {shown.length === vendors.length
                ? `${vendors.length} vendors`
                : `${shown.length} of ${vendors.length} vendors`}
            </span>
          </div>

          {chips.length > 0 && (
            <div className="pl-chips">
              {chips.map(c => (
                <button key={`${c.key}:${c.id}`} type="button" className="pl-chip" onClick={() => toggle(c.key, c.id)}>
                  {c.label} <span aria-hidden="true">×</span>
                </button>
              ))}
              <button type="button" className="pl-linkbtn" onClick={clearAll}>
                Clear all
              </button>
            </div>
          )}

          {shown.length === 0 ? (
            <div className="pl-empty">
              <div className="pl-empty__title">No vendors match</div>
              <div className="bz-muted">Loosen a filter or clear the search.</div>
              <button type="button" className="bz-btn" onClick={clearAll}>
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="bz-card pl-tablecard">
              <table className="bz-table pl-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th style={{ width: 130 }}>Enabled</th>
                    {hasStatusIo && <th style={{ width: 230 }}>Skip status.io check</th>}
                    <th style={{ width: 120 }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(v => {
                    const b = bypassById[v.id];
                    return (
                      <tr
                        key={v.uid}
                        id={`row-${v.uid}`}
                        style={focusUid === v.uid ? { background: 'var(--bg-tan)' } : undefined}
                      >
                        <td>
                          <div className="pl-tproj">
                            <VendorMark vendorId={v.id} label={v.label} size={28} />
                            <div className="pl-tproj__txt">
                              {isGlobalVendor(v) ? (
                                <span className="pl-tproj__name bz-truncate">{v.label}</span>
                              ) : (
                                <Link className="pl-tproj__name bz-truncate" to={`/vendors/${v.id}/configuration`}>
                                  {v.label}
                                </Link>
                              )}
                              <span className="pl-tproj__sub bz-truncate">
                                {v.family ? `${v.id} · ${v.family}` : v.id}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <ItemToggle item={v} withLabel />
                        </td>
                        {hasStatusIo && (
                          <td>
                            {b ? (
                              <div className="bz-row" style={{ gap: 8 }}>
                                <ItemToggle item={b} />
                                {b.value && (
                                  <span className="bz-pill bz-pill--warn">
                                    <span className="bz-dot" />
                                    Bypassed
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="bz-muted" title="No status.io page configured">
                                —
                              </span>
                            )}
                          </td>
                        )}
                        <td className="pl-nowrap bz-muted">{relativeTime(v.updatedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
Vendors.propTypes = {
  sectionId: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  focusUid: PropTypes.string,
  preset: PropTypes.string
};

export default Vendors;
